import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import http from '@/http'

const state: CandidateProfileState = {
  candidateProfile: null,
  candidateProfileItems: null,
  questionGroups: [],
  questionGroupOffset: 0,
  questionOffset: 0,
  isUsingForScreens: false,
  isPreviewMode: false,
  showAutoSaveMsg: false,
  dismissAutoSaveMsg: false,
}

const getters: GetterTree<CandidateProfileState, RootState> = {
  retrieveDataFromProfile: (state) => (sourceKey) => {
    let value = '';
    if (state.candidateProfile) {
      let height = parseInt(state.candidateProfile['height'])
      const weight = parseInt(state.candidateProfile['weight'])
      switch (sourceKey) {
        case "bmi": {
          if (!height && !weight) {
            break
          }
          // convert cm to m
          height = height / 100
          
          const bmi = weight / Math.pow(height, 2)
          value = bmi.toString() || '';
          break;
        }
        default:
          value = state.candidateProfile[sourceKey] || '';
          break;
      }
    }

    return value.toString() || '';
  },
}

const actions: ActionTree<CandidateProfileState, RootState> = {
  fetchList(context, payload) {
    return http.v1.cp.fetchList(payload).then(response => {
      const payload = response.data.data
      const items = payload.data
      return {
        items,
        total: payload.total,
      }
    })
  },
  createForPreview({ commit }, payload) {
    return http.v1.cp.createForPreview(payload).then(response => {
      const payload = response.data.data
      commit('SET_CANDIDATE_PROFILE', payload)
    })
  },
  updateForPreview({ commit }, { object_id, payload }) {
    return http.v1.cp.updateForPreview(object_id, payload).then(response => {
      const payload = response.data.data
      commit('SET_CANDIDATE_PROFILE', payload)
    })
  },
  fetchForUpdate({ commit }) {
    return http.v1.cp.fetchForUpdate().then(response => {
      commit('SET_QUESTION_GROUPS', response.data.data.question_groups)
    })
  },
  fetchForRead({ commit, state }) {
    const candidateProfileObjectId = state.isPreviewMode ? state.candidateProfile?.object_id : null
    return http.v1.cp.fetchForRead(candidateProfileObjectId).then(response => {
      commit('SET_IS_USING_FOR_SCREENS', response.data.data.is_using_for_screens)
      commit('SET_QUESTION_GROUPS', response.data.data.question_groups)
      return response
    })
  },
  fetchForPreview({ commit, state }, payload) {
    return http.v1.cp.fetchForPreview(state.candidateProfile?.object_id, payload).then(response => {
      commit('SET_QUESTION_GROUPS', response.data.data.question_groups)
      return response
    })
  },
  fetchNextQuestion({ commit, state }, payload) {
    const candidateProfileObjectId = state.isPreviewMode ? state.candidateProfile?.object_id : null
    return http.v1.cp.fetchNextQuestion(candidateProfileObjectId, payload).then(response => {
      commit('SET_QUESTION_GROUP_OFFSET', response.data.data.question_group_offset)
      commit('SET_QUESTION_OFFSET', response.data.data.question_offset)
      return response
    })
  },
  fetchPreviousQuestion({ commit, state }, payload) {
    const candidateProfileObjectId = state.isPreviewMode ? state.candidateProfile?.object_id : null
    return http.v1.cp.fetchPreviousQuestion(candidateProfileObjectId, payload).then(response => {
      commit('SET_QUESTION_GROUP_OFFSET', response.data.data.question_group_offset)
      commit('SET_QUESTION_OFFSET', response.data.data.question_offset)
      return response
    })
  },
  updateAnswer({ commit, state }, answer) {
    const candidateProfileObjectId = state.isPreviewMode ? state.candidateProfile?.object_id : null
    return http.v1.cp.updateAnswer(candidateProfileObjectId, answer).then(response => {
      if(!state.dismissAutoSaveMsg) {
        commit('SET_SHOW_AUTO_SAVE_MSG', true)
      }
      commit('SET_QUESTION_GROUP_OFFSET', response.data.data.question_group_offset)
      commit('SET_QUESTION_OFFSET', response.data.data.question_offset)
      return response
    })
  },
  complete({ commit }) {
    return http.v1.cp.complete().then(response => {
      commit('SET_QUESTION_GROUPS', response.data.data.question_groups)
      return response
    })
  },
  fetchByCandidate({ commit }) {
    return http.v1.cp.fetchByCandidate().then(({ data }) => {
      const payload = data.data
      commit('SET_CANDIDATE_PROFILE', payload)
    })
  },
  candidateProfileKeyCheck(context, payload) {
    return http.v1.cp.candidateProfileKeyCheck({
      params: payload
    })
  },
  fetchCandidateProfileItems({ commit }) {
    return http.v1.cp.fetchCandidateProfileItems().then(response => {
      commit('SET_CANDIDATE_PROFILE_ITEMS', response.data.data)
    })
  },
  dismissAutoSaveMsg({ commit }) {
    commit('SET_SHOW_AUTO_SAVE_MSG', false)
    commit('SET_DISMISS_AUTO_SAVE_MSG', true)
  }
}

const mutations: MutationTree<CandidateProfileState> = {
  SET_IS_PREVIEW_MODE(state, payload) {
    state.isPreviewMode = payload
  },
  SET_IS_USING_FOR_SCREENS(state, payload) {
    state.isUsingForScreens = payload
  },
  SET_QUESTION_GROUPS(state, payload) {
    state.questionGroups = payload
  },
  UPDATE_PROGRESS(state, payload) {
    if (payload.last_question_group_object_id && payload.last_question_group_progress) {
      const lastQuestionGroupOffset = state.questionGroups.findIndex(qg => qg.object_id === payload.last_question_group_object_id)
      if (lastQuestionGroupOffset > -1) {
        state.questionGroups[lastQuestionGroupOffset].progress = payload.last_question_group_progress
      } else {
        console.warn('Last Question Group Not Found')
      }
    }

    state.questionGroups[payload.question_group_offset].progress = payload.question_group_progress
  },
  SET_QUESTION_GROUP_OFFSET(state, payload) {
    state.questionGroupOffset = payload
  },
  SET_QUESTION_OFFSET(state, payload) {
    state.questionOffset = payload === false ? -1 : payload
  },
  CLEAR_STATE(state) {
    state.questionGroups = []
    state.showAutoSaveMsg = false
    state.dismissAutoSaveMsg = false
  },
  SET_CANDIDATE_PROFILE(state, payload) {
    state.candidateProfile = payload
  },
  SET_CANDIDATE_PROFILE_ITEMS(state, payload) {
    state.candidateProfileItems = payload || []
  },
  INIT_CANDIDATE_PROFILE(state, payload) {
    state.candidateProfile = payload
  },
  SET_SHOW_AUTO_SAVE_MSG(state, payload) {
    state.showAutoSaveMsg = payload
  },
  SET_DISMISS_AUTO_SAVE_MSG(state, payload) {
    state.dismissAutoSaveMsg = payload
  }
}

const candidateProfile: Module<CandidateProfileState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}

export default candidateProfile